// @import
import { ensureHTMLElement, ensureHTMLNodeList } from "@src/js/lib/ensure";
import { addDotBtnsAndClickHandlers, addPrevNextBtnsClickHandlers } from "@src/js/lib/slider";
import Alpine from "alpinejs";
import EmblaCarousel, { EmblaCarouselType } from "embla-carousel";

type ListingEquipmentsState = {
  isModalOpen: boolean;
  modalId: string | null;
  openModal(id: string): void;
  closeModal(): void;
};

Alpine.store("listingEquipments", {
  modalId: null,
  openModal(id: string) {
    this.modalId = id;
  },
  closeModal() {
    this.modalId = null;
  },
  get isModalOpen() {
    return !!this.modalId;
  },
} as ListingEquipmentsState);

// Listing slider
Alpine.directive("listing-equipments", (element, {}, { cleanup }) => {
  const dom = {
    viewportNode: ensureHTMLElement(element.querySelector(".c-listing-equipments__slider-viewport")),
    slides: ensureHTMLNodeList(element.querySelectorAll(".c-listing-equipments__equipment")),
    prevBtnNode: ensureHTMLElement(element.querySelector(".c-listing-equipments__slider-btn-prev")),
    nextBtnNode: ensureHTMLElement(element.querySelector(".c-listing-equipments__slider-btn-next")),
  };

  const state = Alpine.store("listingEquipments") as ListingEquipmentsState;
  const slider = EmblaCarousel(dom.viewportNode, { align: "start" });

  const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(slider, {
    prevBtn: dom.prevBtnNode,
    nextBtn: dom.nextBtnNode,
  });

  function onSlideClick(slide: HTMLElement) {
    const equipmentId = slide.getAttribute("data-equipment-id");
    state.openModal(equipmentId ?? "");
  }

  dom.slides.forEach((slide) => {
    const element = slide as HTMLElement;
    const hasDescription = element.dataset.hasDescription === "true";
    if (!hasDescription) {
      return;
    }
    element.addEventListener("click", () => onSlideClick(element));
  });

  slider.on("destroy", removePrevNextBtnsClickHandlers);

  cleanup(() => {
    slider.destroy();
  });
});

// Modal slider
Alpine.directive("listing-equipments-modal", (element, { expression }, { effect, cleanup }) => {
  const equipmentId = expression;
  const state = Alpine.store("listingEquipments") as ListingEquipmentsState;

  const dom = {
    viewportNode: element.querySelector<HTMLElement>(".c-listing-equipments__modal-viewport"),
    paginationNode: element.querySelector<HTMLElement>(".c-slider-pagination"),
  };

  let slider: EmblaCarouselType | null = null;

  function showModal() {
    if (!slider && dom.viewportNode) {
      slider = EmblaCarousel(dom.viewportNode, { align: "start" });
      if (dom.paginationNode) {
        const removePaginationHandlers = addDotBtnsAndClickHandlers(slider, {
          dotsNode: dom.paginationNode,
        });
        slider.on("destroy", removePaginationHandlers);
      }
    }
    element.classList.add("c-listing-equipments__modal--open");
  }

  function hideModal() {
    element.classList.remove("c-listing-equipments__modal--open");
  }

  effect(() => {
    if (state.modalId === equipmentId) {
      showModal();
    } else {
      hideModal();
    }
  });

  function onOverlayClick(event: MouseEvent) {
    if (event.target === element) {
      state.closeModal();
    }
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      state.closeModal();
    }
  }

  element.addEventListener("click", onOverlayClick);
  window.addEventListener("keydown", onKeydown);

  cleanup(() => {
    element.removeEventListener("click", onOverlayClick);
    window.removeEventListener("keydown", onKeydown);
    slider?.destroy();
  });
});
